// StrokeRenderer — coordinates the pen-specific commit cadence that
// distinguishes the MVP pens (architecture.md §19.3, consult 2026-05-09 ペン統合).
//
//   brush  : commit per `stroke.segment` (low buffer cost, immediate display)
//            旧 `'normal'` + `'water'` を統合. alpha 値で不透明線画から
//            build-up 半透明まで連続化する想定 (= Step 3 で累乗カーブ実装).
//   flat   : NO per-segment commit. Buffer accumulates across the whole
//            stroke (max 蓄積モード). Final commit fires on `stroke.end`.
//            旧 `'alpha'` (一般透過) から rename. マスク・逆マスクの基盤.
//            (architecture.md §19.3 / §19.7)
//
//            **Stage Y (consult 2026-05-31, `marker-pen-deprecation-plan.md`)**:
//            `'flat'` と `'marker'` の per-stamp alpha 計算は **完全に同一**
//            (= 両方とも `alphaScale = pt.pAlpha`). 両者の差は protocol enum
//            に `'marker'` が残っていることだけで、内部挙動は統合済. 既存
//            `'flat'` ユーザは `DEFAULT_PEN_PRESSURE.flat.alphaEnabled=false`
//            (default) + `computeEffectivePressure` で `pAlpha=1.0` が明示
//            送信されるため、`alphaScale=1.0` で従来 uniform alpha 挙動が
//            完全互換. `'marker'` の protocol 撤去は Stage X (β 期 = Phase 10)
//            で実施予定 (= `marker-pen-deprecation-plan.md` 参照).
//   marker : (Stage Y で `'flat'` と統合済、撤去予定 — 上記参照.)
//            形式的には NO per-segment commit + `'max'` 蓄積モード + stroke-end
//            commit cadence + alpha 軸 `pt.pAlpha` 連動 (= `'flat'` と同じ).
//            コピックマーカー風の独自表現 (consult 2026-05-09 試験追加,
//            architecture.md §19.3).
//   eraser : direct destination-out on main; buffer not used.
//
// Each stroke (= sid) owns its OWN alpha buffer, and several strokes can be
// in flight on the same layer at once (= self + remote peers drawing
// together). In-flight deferred-commit strokes are composited per-region so
// overlapping ones stay correct. The collision rule is keyed on **sid**, not
// on the layer: if `startStroke` arrives for a sid that already exists, the
// new stroke wins and the old one is committed (shouldn't happen for normal
// local strokes, but can during replay / reconnect).
//
// (architecture.md §19.3 / §19.5 / §19.7 / §19.10 A)
import { DRAWING_PERF_MEASURE_NAMES, measureDrawingHotPath } from './perfMeasure.js';
import { clearStrokeBuffer, commitBufferToLayer, commitBufferToSubrect, createStrokeBuffer, eraseDot, eraseLine, perStampAlphaFor, } from './rasterizer.js';
import { resolveCommitTexture, resolveStampRenderer, } from './stamp/StampRenderer.js';
import { sampleTextureAlpha } from './stamp/texture/textureSampler.js';
/**
 * Does this pen DEFER its buffer commit to a single stroke-end event, rather
 * than committing per-segment? `'flat'` / `'marker'` (uniform alpha) and
 * `'tone'` (screentone陰影, deferred so crossing segments don't source-over
 * build-up mid-stroke, texture-brush-design.md §12.2) / `'pastel'`
 * (phase11-light-pens-2026-08-21.md §2.5.5 — its commit-time texture multiply
 * must run exactly once per stroke). `'brush'` / `'texture'` / `'crayon'` /
 * `'airbrush'` / `'dot'` commit per-segment; `'eraser'` is destination-out
 * (never buffered).
 *
 * **This predicate is ONLY about commit cadence now** (2026-08-21, §2.5.5).
 * Until then it ALSO doubled as the buffer accumulate-mode selector (every
 * pen in this group used `'max'`) — see `accumulateModeFor`'s doc for the
 * full history of why pastel forced those two concerns apart: pastel wants
 * deferred commit (this predicate → `true`) but `'add'` build-up accumulate
 * (`accumulateModeFor` → `'add'`), a combination a single boolean can't
 * express. Do NOT infer accumulate mode from this function's result anymore
 * — call `accumulateModeFor` for that.
 */
export const isFlatPen = (pen) => pen === 'flat' || pen === 'marker' || pen === 'tone';
/**
 * Buffer accumulate mode for a pen (phase11-light-pens-2026-08-21.md §2.5.5).
 * `'max'` for `'flat'` / `'marker'` (uniform alpha — repeated stamps on the
 * same pixel must NOT pile up, architecture.md §19.3) and `'tone'`
 * (screentone陰影 stays a uniform shade under re-crossed dots). `'add'`
 * (source-over build-up) for everything else, INCLUDING `'pastel'` as of
 * §2.5.5 — see `createPastelStampRenderer`'s / `resolveCommitTexture`'s doc
 * (`StampRenderer.ts`) for why moving the texture multiply to the commit step
 * (ONE multiply per stroke, not ~2r per stroke) let pastel go back to plain
 * build-up without re-compressing the grain's texel contrast the way the OLD
 * per-dab `'add'` + alpha-multiply combination did (§2.3 ⚠️'s
 * `1-(1-s·t)^N` diagnosis).
 *
 * Split out from `isFlatPen` (2026-08-21): before this, ALL deferred-commit
 * pens shared `'max'` and the two concerns lived behind one boolean
 * (`stroke.flat`). Pastel is the first pen that wants deferred commit
 * (`isFlatPen` → `true`, so the buffer's single texture multiply still lands
 * exactly once) paired with `'add'` accumulate (build-up within the stroke)
 * — hence two independent resolvers instead of one.
 */
const accumulateModeFor = (pen) => pen === 'flat' || pen === 'marker' || pen === 'tone' ? 'max' : 'add';
const NO_DIRTY = { x0: -1, y0: -1, x1: -1, y1: -1 };
/**
 * Per-pen × per-axis gamma table for the unified pressure mapping
 *
 *   effective = base * (min + (1 - min) * pow(p, gamma))
 *
 * defined in architecture.md §19.15. The mapping itself is **applied
 * send-side** by the web layer's `computeEffectivePressure` (= the wire
 * value `stroke.segment.p` is already the post-mapping effective `p`,
 * the rasterizer/strokeRenderer must NOT re-apply `pow(p, gamma)` to
 * avoid double mapping). This table lives in drawing-core purely as
 * the **single source of truth** the web layer imports from, so the
 * gamma constants stay co-located with the rasterizer they describe
 * (= 二重管理回避). drawing-core itself reads `pt.p` at face value
 * without consulting this table.
 *
 * **全要素 gamma=1 (線形)** (consult 2026-05-09 ペン統合): 旧 `normal.alpha = 3`
 * の累乗 gamma 例外は **撤廃** された. ペン統合で `'brush'` の per-stamp alpha
 * 自体が累乗カーブ (Step 3 で実装) で連続化されるため, 筆圧 mapping 側で別途
 * 累乗を入れる必要がない (= 二重累乗回避). 「不透明線画 (alpha=255) の入り抜き
 * 効果」は `'brush'` で alpha 筆圧 ON にすれば自然に得られる. 詳細は
 * architecture.md §19.15 / §19.3 / session-2026-05-08.md §0.5 参照.
 *
 * - `'brush'`: width / alpha 両軸とも線形 (= 既存挙動と pow(p, 1) === p で一致).
 * - `'flat'`: width / alpha 両軸とも線形.
 * - `'marker'`: width / alpha 両軸とも線形 (= consult 2026-05-09 試験追加、
 *   `'flat'` と同じ表エントリ. 違いは `processPoint` の alpha 軸を `pt.pAlpha`
 *   で動的変動させるかどうか — `'marker'` は動かす、`'flat'` は固定 1.0).
 * - `'eraser'`: width 軸のみ実効、alpha 軸は strength 固定で使わないが、表として
 *   整合させるため宣言.
 *
 * α 期は const, β 期で `gamma` を CSP 風スプラインカーブの制御点 Y 座標に
 * 展開して任意カーブへ拡張予定 (= ユーザ設定 `(min, gamma)` は β に無変換で
 * 持ち越せる).
 *
 * (architecture.md §19.15, consult 2026-05-09)
 */
export const PRESSURE_GAMMA = {
    brush: { width: 1, alpha: 1 },
    flat: { width: 1, alpha: 1 },
    marker: { width: 1, alpha: 1 },
    eraser: { width: 1, alpha: 1 },
    // テクスチャブラシ (texture-brush-design.md §6): width / alpha 両軸とも線形
    // (= `'brush'` と同じ). 筆圧 → texture 濃度連動は MVP では入れず alpha 軸に
    // 委ねる (要検証で後付け可、§6).
    texture: { width: 1, alpha: 1 },
    // スクリーントーン (texture-brush-design.md §12.2): width / alpha 両軸とも
    // 線形 (= brush と同じ表エントリ). 筆圧グラデは不採用 (consult 2026-06-20) —
    // density は penParams から stroke 単位で固定し、筆圧は太さ/濃さの素直な連動
    // に委ねる. 段階2 の軸ドラッグ濃度ランプは density 側の拡張で別途.
    tone: { width: 1, alpha: 1 },
    // エアブラシ (airbrush-design-2026-08-15.md): width / alpha 両軸とも線形
    // (= `'brush'`/`'texture'` と同じ表エントリ). 濃度の実体は per-stamp alpha
    // ではなく閾値ハッシュの ink/no-ink 判定 (`StampParams.airbrush`) が担うので、
    // 筆圧はここでは他の build-up 系ペンと同じ素直な線形連動に委ねる.
    airbrush: { width: 1, alpha: 1 },
    // クレヨン / パステル / ドット (phase11-light-pens-2026-08-21.md §1): 3 本とも
    // width / alpha 両軸線形 (= `'brush'`/`'texture'`/`'airbrush'` と同じ表エントリ).
    // クレヨンの被覆連動は筆圧軸ではなく §2.4 のフォールバック (stroke 単位の
    // alpha → densityLevel) で実現するため、筆圧マッピング自体は他の build-up 系
    // ペンと揃える. ドットは間引きデコレータの内側が普通のブラシ stamp なので同様.
    crayon: { width: 1, alpha: 1 },
    pastel: { width: 1, alpha: 1 },
    dot: { width: 1, alpha: 1 },
};
/**
 * Does this stroke defer its single buffer commit to `stroke.end` (= it is NOT
 * yet reflected in `layer.main` while in flight, so the display compositor must
 * blend its buffer on top)? `'flat'` / `'marker'` / `'tone'` / `'pastel'` do
 * (= `s.flat`, texture-brush-design.md §12.2 / phase11-light-pens-2026-08-21.md
 * §2.3 ⚠️). `'brush'` / `'texture'` / `'crayon'` commit per segment → already
 * in main.
 */
const isDeferredCommit = (s) => s.flat;
const expandBbox = (a, b) => {
    if (b.x0 < 0)
        return a;
    if (a.x0 < 0)
        return b;
    return {
        x0: Math.min(a.x0, b.x0),
        y0: Math.min(a.y0, b.y0),
        x1: Math.max(a.x1, b.x1),
        y1: Math.max(a.y1, b.y1),
    };
};
/**
 * Coordinator over `Layer[]` + per-sid `StrokeState[]`. Pure data — the
 * engine class wraps this with display-canvas blit logic.
 */
export class StrokeRenderer {
    // public-ish so tests can poke at layers / strokes; the engine class is
    // the actual external surface so this is fine.
    layers = [];
    strokes = new Map();
    /** Aggregate dirty bbox in main-canvas coordinates since last flush. */
    accumulatedDirty = NO_DIRTY;
    /**
     * Per-layer dirty bbox of `Layer.main` writes (layer-thumbnails-2026-10-13.md
     * 「層ごとの dirty bbox」). Separate from `accumulatedDirty` (the DISPLAY bbox,
     * which also carries non-`main` things such as the deferred-commit footprint
     * and which `EngineCore.flush` resets every frame): this one only grows when a
     * layer's committed pixels really change, and is drained by the thumbnail
     * pipeline (`takeLayerDirty`) at its own pace. Indexed by `layerId`, sparse
     * (missing = clean). Pure bookkeeping — nothing in the display path reads it.
     */
    layerDirty = [];
    /**
     * Per-layer snapshot buffer for **normal mask** compare (Java 互換 D'' rule,
     * mask-java-compat-2026-05-21.md §3.1 / §6.2). Allocated lazily the first
     * time a normal-mask stroke begins on that layer, then **reused** for
     * subsequent normal-mask strokes (= GC pressure ゼロ). The MVP runs one
     * stroke per layer at a time, so reuse is safe; β multi-stroke per layer
     * would need a per-stroke alloc instead, but that's not a concern today.
     *
     * Memory cost: +1 layer-sized `Uint8ClampedArray` per layer that's ever
     * used a normal-mask stroke (= ~2-5 MB per layer on MVP canvas sizes).
     * Mobile-monitor as needed — the memo §6.3 allows a per-stroke alloc
     * fallback if footprint becomes an issue.
     */
    maskSnapshots = new Map();
    /**
     * Undo B' bbox-local repair write clip (docs/_wip/undo-conflict-aware-
     * 2026-07-12.md「stamp 書き込みの bbox clip」/ 「実装フェーズ検証結果」2000²
     * timing: 交差 stroke 8 本・太筆で 56.8ms → clip 後の実測は再計測記録参照).
     * `null` (default, EVERY live drawing engine) = unclipped, byte-identical
     * to the pre-clip renderer. Only `UndoBaselineStore`'s dedicated scratch
     * `EngineCore` ever calls `setWriteClip` — it is never touched by the
     * normal `stroke.*` broadcast path.
     */
    writeClip = null;
    constructor(layers) {
        this.layers = layers;
    }
    /**
     * Restrict every subsequent stamp / erase write to `clip` (inclusive
     * layer-space rect) until cleared with `null`. Coordinates are NEVER
     * transformed — sampling (mask compare, texture/tone world/stamp mapping)
     * still reads each pixel's TRUE coordinate, only the final write is
     * gated (rasterizer.ts `StampParams.clip` header has the full contract).
     * `commitBufferToLayer`'s cost is bounded FOR FREE by this: a clipped
     * stroke's `buffer.dirty` only ever grows over pixels actually written,
     * so a commit-at-end pen (`flat`/`marker`/`tone`) whose long path barely
     * grazes `clip` still only iterates the small clipped region at commit
     * time — no separate clip plumbing needed in the commit step itself.
     */
    setWriteClip(clip) {
        this.writeClip = clip;
    }
    /**
     * Lazily allocate (or reuse) the per-layer normal-mask snapshot, then copy
     * `layer.main` into it. Returns the snapshot ready to be referenced by
     * the in-progress stroke's `maskSnapshot` field.
     *
     * The snapshot is sized to match `layer.main.length`; if a layer were ever
     * resized (= future feature) the cached snapshot would be discarded.
     */
    acquireMaskSnapshot(layer) {
        let snapshot = this.maskSnapshots.get(layer.id);
        if (snapshot === undefined || snapshot.length !== layer.main.length) {
            snapshot = new Uint8ClampedArray(new ArrayBuffer(layer.main.length));
            this.maskSnapshots.set(layer.id, snapshot);
        }
        snapshot.set(layer.main);
        return snapshot;
    }
    /** Begin a stroke. Rejects if `layerId` is out of range. */
    startStroke(args) {
        const layer = this.layers[args.layerId];
        if (layer === undefined)
            return;
        // If a stroke with this sid already exists, commit + drop it. This
        // shouldn't happen for normal local strokes but can occur during
        // remote-stroke replay if the server ever resends a `stroke.start`.
        const existing = this.strokes.get(args.sid);
        if (existing !== undefined) {
            this.endStroke(existing.sid);
        }
        const buffer = args.pen === 'eraser' ? null : createStrokeBuffer(layer.width, layer.height);
        // (`'brush'` と `'flat'` は buffer を持つ; `'eraser'` のみ destination-out で
        //  main へ直接書き込むため buffer 不要.)
        // Normal mask only: capture a snapshot of `layer.main` so per-stamp mask
        // compares read the **stroke-start state** instead of the live (= self-
        // mutating) main. This is the D'' rule (mask-java-compat-2026-05-21.md
        // §3.1) — the Java self-protect mechanism translated into the Node
        // transparent-init model. Inverse mask intentionally keeps live-read
        // (= 自己破壊機構).
        const maskSnapshot = args.mask.type === 'normal' ? this.acquireMaskSnapshot(layer) : null;
        this.strokes.set(args.sid, {
            sid: args.sid,
            pen: args.pen,
            color: args.color,
            alpha: args.alpha,
            width: args.width,
            layerId: args.layerId,
            mask: args.mask,
            aa: args.aa ?? false,
            buffer,
            maskSnapshot,
            lastPoint: null,
            lastPressure: null,
            lastPressureAlpha: null,
            lastActivityMs: args.nowMs,
            // airbrush-design-2026-08-15.md §1.2: 0-based wire-point counter, only
            // meaningful for `pen='airbrush'` but tracked unconditionally (see
            // `StrokeState.pointIndex` doc).
            pointIndex: 0,
            // Resolve the pen kernel ONCE here (texture-brush-design.md §5). The
            // per-stamp hot loop then calls `stroke.stampRenderer.dot/line` —
            // monomorphic per stroke, no per-stamp branch on `pen`. `penParams`
            // selects the texture preset (sampler captured by the renderer).
            stampRenderer: resolveStampRenderer(args.pen, args.penParams, args.alpha),
            // texture-brush-design.md §12.2: トーン (= flat/marker と同じ陰影系) は
            // 遅延 commit. Resolve once here so the per-stamp path just reads a
            // boolean. (Accumulate mode is a SEPARATE resolve — `accumulateModeFor`
            // — since §2.5.5; see `isFlatPen`'s doc.)
            flat: isFlatPen(args.pen),
            // phase11-light-pens-2026-08-21.md §2.5.5: `undefined` for every pen
            // except `'pastel'`. Resolved once here so the commit path just forwards
            // an immutable reference.
            commitTexture: resolveCommitTexture(args.pen, args.penParams),
        });
    }
    /**
     * Append points to an active stroke. Renders each segment immediately
     * (`'brush'` commits per segment, `'flat'` buffers without commit, `'eraser'`
     * writes destination-out directly to main).
     */
    addPoints(sid, points, nowMs) {
        const stroke = this.strokes.get(sid);
        if (stroke === undefined)
            return;
        stroke.lastActivityMs = nowMs;
        if (points.length === 0)
            return;
        const layer = this.layers[stroke.layerId];
        if (layer === undefined)
            return;
        for (const pt of points) {
            this.processPoint(stroke, layer, pt);
        }
    }
    /**
     * Finalise a stroke. For `alpha` pens this is where the buffer commits
     * to main for the first time. After this the sid is unknown.
     *
     * Idempotent: calling it twice or with an unknown sid is a no-op (the
     * §19.7 watchdog and disconnect-driven force-end use that property).
     */
    endStroke(sid) {
        const stroke = this.strokes.get(sid);
        if (stroke === undefined)
            return;
        const layer = this.layers[stroke.layerId];
        if (layer !== undefined && stroke.buffer !== null) {
            // For `'flat'` pens the per-segment commits never happened, so commit
            // now. For `'brush'` this commit is empty (buffer is clean).
            const before = this.accumulatedDirty;
            const bufBboxBefore = { ...stroke.buffer.dirty };
            commitBufferToLayer({
                layer,
                buffer: stroke.buffer,
                color: stroke.color,
                userAlpha: 255,
                commitTexture: stroke.commitTexture,
            });
            // commit doesn't track its own dirty; reuse the buffer's pre-commit bbox.
            this.accumulatedDirty = expandBbox(before, normaliseBbox(bufBboxBefore));
            this.markLayerDirty(stroke.layerId, bufBboxBefore);
            clearStrokeBuffer(stroke.buffer);
        }
        this.strokes.delete(sid);
    }
    /** Apply a `whiteboard.clear`. MVP clears every layer; β can use layerId. */
    clearLayer(layerId, fillRgb, fillAlpha) {
        const ids = layerId === undefined ? this.layers.map((l) => l.id) : [layerId];
        for (const id of ids) {
            const layer = this.layers[id];
            if (layer === undefined)
                continue;
            const r = (fillRgb >> 16) & 0xff;
            const g = (fillRgb >> 8) & 0xff;
            const b = fillRgb & 0xff;
            const data = layer.main;
            for (let i = 0; i < data.length; i += 4) {
                data[i] = r;
                data[i + 1] = g;
                data[i + 2] = b;
                data[i + 3] = fillAlpha;
            }
            this.accumulatedDirty = expandBbox(this.accumulatedDirty, {
                x0: 0,
                y0: 0,
                x1: layer.width - 1,
                y1: layer.height - 1,
            });
            this.markLayerDirty(id, { x0: 0, y0: 0, x1: layer.width - 1, y1: layer.height - 1 });
        }
        // Drop strokes that were targeting cleared layers — they no longer
        // make sense after a clear.
        for (const [sid, stroke] of this.strokes) {
            if (layerId === undefined || stroke.layerId === layerId) {
                if (stroke.buffer !== null)
                    clearStrokeBuffer(stroke.buffer);
                this.strokes.delete(sid);
            }
        }
    }
    /**
     * Snapshot + clear the accumulated dirty bbox. The engine calls this
     * each rAF tick to know what region to copy from the main `Layer.main`
     * to the display canvas.
     */
    flushDirtyBbox() {
        const out = this.accumulatedDirty;
        this.accumulatedDirty = NO_DIRTY;
        return out;
    }
    /**
     * Expand the accumulated dirty bbox by an externally-known region without
     * going through a stroke. Used by `EngineCore.applyLayerRegion` (conflict-
     * aware Undo B' bbox-local rewind, docs/_wip/undo-conflict-aware-2026-07-12.md
     * 「クライアント側の復元コスト」) so a direct `Layer.main` patch (bypassing the
     * normal stamp path) still gets picked up by the next `flush()`.
     */
    markDirty(region) {
        if (region.x0 < 0)
            return;
        this.accumulatedDirty = expandBbox(this.accumulatedDirty, region);
    }
    /**
     * Record that `layerId`'s committed pixels (`Layer.main`) changed inside
     * `region` (inclusive, layer-space). Called by every write path in this class
     * and by `EngineCore` for its direct `Layer.main` writes (snapshot / layer
     * region / imagepaste). Empty regions (`x0 < 0`) are ignored.
     */
    markLayerDirty(layerId, region) {
        if (region.x0 < 0)
            return;
        this.layerDirty[layerId] = expandBbox(this.layerDirty[layerId] ?? NO_DIRTY, region);
    }
    /** Whether any layer has undrained dirty pixels. */
    hasLayerDirty() {
        for (const b of this.layerDirty) {
            if (b !== undefined && b.x0 >= 0)
                return true;
        }
        return false;
    }
    /**
     * Take + reset `layerId`'s dirty bbox (`x0 < 0` = clean). Independent of
     * `flushDirtyBbox` (display).
     */
    takeLayerDirty(layerId) {
        const out = this.layerDirty[layerId] ?? NO_DIRTY;
        this.layerDirty[layerId] = NO_DIRTY;
        return out;
    }
    /** Number of strokes currently in flight (any author). */
    activeStrokeCount() {
        return this.strokes.size;
    }
    /** Test / engine introspection. */
    hasStroke(sid) {
        return this.strokes.has(sid);
    }
    /** Force-end any stroke whose last activity is older than `cutoffMs`. */
    forceEndIdle(cutoffMs) {
        const ended = [];
        for (const [sid, stroke] of this.strokes) {
            if (stroke.lastActivityMs < cutoffMs) {
                this.endStroke(sid);
                ended.push(sid);
            }
        }
        return ended;
    }
    /**
     * Composite all active "deferred-commit" stroke buffers (= `'flat'` and
     * `'marker'`) into a temporary RGBA buffer that mirrors `layer.main`
     * plus the in-flight contribution. Used for display rendering
     * (architecture.md §19.6) and for the Phase 5 snapshot integrity step
     * (§19.9).
     *
     * Returns the layer's `main` directly when no deferred-commit stroke is
     * in flight, so the common path allocates nothing.
     *
     * Per-call `performance.measure` instrumentation, gated by the module flag
     * in `perfMeasure.ts` (= default OFF, single boolean check pass-through).
     * The e2e harness (`drawingPerfHarness.spec.ts` + the PR 1.6 verify
     * candidates) flips it ON to count compositor cost vs `drawLine` /
     * `eraseLine`. trace 解析 (= drawing-perf-trace-analysis-2026-05-17.md)
     * で立てた「`compositeForDisplay` が真の perf 主犯」仮説の検証経路.
     */
    compositeForDisplay(layerId) {
        // `measureDrawingHotPath` は void-returning fn 用のラッパなので closure
        // で out param を受け渡す. ENABLED=false の hot path では boolean check
        // 1 個 + fn() 呼び出しに圧縮されるので overhead は無視できる. (perfMeasure
        // .ts コメントの「production overhead は boolean check 1 個」と整合.)
        let result = null;
        measureDrawingHotPath(DRAWING_PERF_MEASURE_NAMES.compositeForDisplay, () => {
            result = this.compositeForDisplayImpl(layerId);
        });
        // After measure-wrapped fn ran, `result` is always set (the impl always
        // returns a PixelBuffer, even an empty one for unknown layerIds). Cast
        // through `unknown` because TS narrows `null` from the let-init too
        // aggressively across the closure boundary.
        return result;
    }
    compositeForDisplayImpl(layerId) {
        const layer = this.layers[layerId];
        if (layer === undefined)
            return new Uint8ClampedArray(new ArrayBuffer(0));
        // `'flat'` (旧 `'alpha'` from rename) と `'marker'` (consult 2026-05-09 試験
        // 追加) は stroke 終端で 1 回だけ commit するので、in-flight (start..end の
        // 最中) は main にまだ反映されていない. 表示時はここで composite してユーザに
        // 見せる. (architecture.md §19.6 / §19.9)
        const inflightDeferred = [...this.strokes.values()].filter((s) => isDeferredCommit(s) && s.layerId === layerId && s.buffer !== null);
        if (inflightDeferred.length === 0)
            return layer.main;
        const copy = new Uint8ClampedArray(new ArrayBuffer(layer.main.length));
        copy.set(layer.main);
        const out = copy;
        for (const stroke of inflightDeferred) {
            if (stroke.buffer === null)
                continue;
            // Reuse the rasterizer's commit, but on the temporary buffer copy.
            commitBufferToLayer({
                layer: { ...layer, main: out },
                buffer: stroke.buffer,
                color: stroke.color,
                userAlpha: 255,
                // phase11-light-pens-2026-08-21.md §2.5.5: an in-flight pastel stroke
                // must preview WITH its grain, not just at the final commit — see
                // `StrokeState.commitTexture`'s doc.
                commitTexture: stroke.commitTexture,
            });
        }
        return out;
    }
    /**
     * Bbox-limited variant of `compositeForDisplay` (PR 2-α 2026-05-17 —
     * `compositeForDisplay` の dirty-rect 化).
     *
     * The default `compositeForDisplay(layerId)` allocates a full-canvas
     * RGBA buffer + memcpys the entire `layer.main` into it before applying
     * in-flight `'flat'`/`'marker'` stroke overlays. On a 4000² canvas that
     * is 64 MB alloc + 64 MB copy **per rAF tick**, which trace analysis
     * (= `docs/_wip/perf-baseline-alpha-workload-2026-05-17.md`) showed
     * dominates the main-thread budget at α-tier workloads (= 1700-1900 ms
     * `compSum` for a single 1000-point stroke on 4000² canvas).
     *
     * This Region variant restricts both the alloc and the iteration to a
     * caller-supplied `region` (typically the engine's accumulated dirty
     * bbox from `flushDirtyBbox`). The caller-visible behaviour for pixels
     * inside the region is bit-identical to `compositeForDisplay` —
     * specifically:
     *
     *   - Pixels of `layer.main` inside the region are copied verbatim into
     *     the result buffer.
     *   - All in-flight deferred-commit strokes targeting this layer are
     *     blended via the exact same `commitBufferToSubrect` math used by
     *     `commitBufferToLayer`. The dirty bbox of each stroke is clipped to
     *     the region — strokes whose dirty area sits entirely outside the
     *     region contribute nothing.
     *
     * The returned `rgba` buffer is `region` dimensions, **not** full-canvas
     * (= the alloc saving is the whole point). The caller is responsible
     * for forwarding `(rgba, x, y, w, h)` to its backend (= what
     * `EngineCore.flush` already does — it previously did a full-canvas
     * `compositeForExport` and `subarray`-copied the bbox out).
     *
     * Per-call `performance.measure` instrumentation reuses the same
     * `oekaki.drawing.compositeForDisplay` name as the full-canvas variant
     * so the harness (`drawingPerfHarnessAlphaWorkload.spec.ts`) can compare
     * PR 1.7 baseline vs PR 2-α numbers directly without changing the
     * filter. The before/after `compSum` collapses to the dirty-rect cost
     * (= O(bbox area) instead of O(canvas area)).
     *
     * Region is `{ x0, y0, x1, y1 }` inclusive layer-space coords. Out-of-
     * bounds regions are clamped to the layer; an empty region (= `x0 < 0`
     * or `x0 > x1`) returns an empty buffer with `w = h = 0`.
     *
     * Note: the returned buffer never aliases `layer.main`. We always alloc
     * + copy the requested sub-rect, even when no deferred-commit stroke is
     * in flight. The caller may safely mutate the result (e.g. overlay
     * imagepaste preview pixels) without affecting layer state.
     */
    compositeForDisplayRegion(layerId, region) {
        let result = null;
        measureDrawingHotPath(DRAWING_PERF_MEASURE_NAMES.compositeForDisplay, () => {
            result = this.compositeForDisplayRegionImpl(layerId, region);
        });
        return result;
    }
    compositeForDisplayRegionImpl(layerId, region) {
        const layer = this.layers[layerId];
        const empty = {
            rgba: new Uint8ClampedArray(new ArrayBuffer(0)),
            x: 0,
            y: 0,
            w: 0,
            h: 0,
        };
        if (layer === undefined)
            return empty;
        // Empty/sentinel bbox (= NO_DIRTY from `flushDirtyBbox` when nothing was
        // touched): the accumulator uses `x1 = -1` to signal "no region", so
        // gate on the upper bound — a region whose top-left is < 0 but whose
        // bottom-right is in-range is still a meaningful clipped region.
        if (region.x1 < 0 || region.y1 < 0 || region.x0 > region.x1 || region.y0 > region.y1) {
            return empty;
        }
        // Clamp the requested region to layer bounds. The dirty-bbox accumulator
        // is supposed to keep things in-range already, but the engine occasionally
        // accumulates `clearLayer`-derived bboxes that match the layer extent
        // exactly — saturating clamp keeps this method robust to off-by-one
        // surprises.
        const rx0 = region.x0 < 0 ? 0 : region.x0;
        const ry0 = region.y0 < 0 ? 0 : region.y0;
        const rx1 = region.x1 > layer.width - 1 ? layer.width - 1 : region.x1;
        const ry1 = region.y1 > layer.height - 1 ? layer.height - 1 : region.y1;
        if (rx0 > rx1 || ry0 > ry1)
            return empty;
        const w = rx1 - rx0 + 1;
        const h = ry1 - ry0 + 1;
        const out = new Uint8ClampedArray(new ArrayBuffer(w * h * 4));
        // Row-by-row copy from layer.main into the sub-rect. Using
        // `subarray + set` triggers the typed-array C-level memcpy, so this is
        // one memcpy per row of `h`. For a bbox of 200×200 on 4000² canvas
        // that's ~200×800 bytes ≈ 160 KB total — vs 64 MB for the full canvas.
        const rowBytes = w * 4;
        const layerW = layer.width;
        for (let y = 0; y < h; y++) {
            const srcStart = ((ry0 + y) * layerW + rx0) * 4;
            out.set(layer.main.subarray(srcStart, srcStart + rowBytes), y * rowBytes);
        }
        // Apply any in-flight deferred-commit strokes. Each stroke's dirty bbox
        // is clipped to `(rx0..rx1, ry0..ry1)` inside `commitBufferToSubrect`
        // so strokes whose drawing fell entirely outside the region cost nothing.
        const inflightDeferred = [...this.strokes.values()].filter((s) => isDeferredCommit(s) && s.layerId === layerId && s.buffer !== null);
        for (const stroke of inflightDeferred) {
            if (stroke.buffer === null)
                continue;
            commitBufferToSubrect({
                buffer: stroke.buffer,
                color: stroke.color,
                userAlpha: 255,
                target: out,
                subX0: rx0,
                subY0: ry0,
                subWidth: w,
                subHeight: h,
                // phase11-light-pens-2026-08-21.md §2.5.5: this path also serves the
                // Undo bbox-local repair replay (see `CommitToSubrectParams.commitTexture`
                // doc) — omitting it here would make pastel render correctly at final
                // commit but wrong right after an Undo repair.
                commitTexture: stroke.commitTexture,
            });
        }
        return { rgba: out, x: rx0, y: ry0, w, h };
    }
    // ─── internals ───────────────────────────────────────────────────────────
    processPoint(stroke, layer, pt) {
        // airbrush-design-2026-08-15.md §1.2: capture THIS call's wire-point
        // index (0-based), then advance the counter for the next call. Captured
        // before any pen-specific branching so it stays a plain "how many
        // processPoint calls has this stroke seen" count regardless of pen.
        const pointIndex = stroke.pointIndex;
        stroke.pointIndex += 1;
        // Pressure 0..1 (already-mapped effective scale per architecture.md
        // §19.15 — send-side applied user widthMin/alphaMin, the rasterizer
        // does NOT re-map). `undefined` (= no-pressure device or feature off)
        // collapses to 1.0 → bit-identical to the pre-pressure path.
        const pNow = pt.p ?? 1;
        const pPrev = stroke.lastPressure ?? pNow;
        // Alpha-axis pressure (consult 2026-05-09 / Step 5b — 効果軸独立).
        // `pt.pAlpha` が指定されていればそれを使う (= 2 軸独立). 未指定なら
        // `pt.p` で fallback (= 既存挙動と bit-identical な 1 軸モード). さらに
        // `pt.p` も undefined なら `1.0` (= no-pressure device). この fallback
        // 順により旧 web client (`pt.pAlpha` 未送信) は両軸 `pt.p` で動き、
        // 新 web client (`pt.p` + `pt.pAlpha` 送信) は 2 軸独立に動く.
        const pAlphaNow = pt.pAlpha ?? pt.p ?? 1;
        // 持ち越し: alpha 軸を持っていなければ width 軸 (`lastPressure`) で
        // fallback、それも無ければ現点の `pAlphaNow` (= drawDot 単独点の挙動と
        // 整合).
        const pAlphaPrev = stroke.lastPressureAlpha ?? stroke.lastPressure ?? pAlphaNow;
        // Per-pen mapping table (architecture.md §19.15, consult 2026-05-09 ペン統合).
        //
        //   brush  : width ✅ / alpha ✅  (旧 `'normal'` + `'water'` 統合. alpha 軸は
        //                                  Step 3 で累乗カーブ実装後に build-up が
        //                                  正常動作する. Step 2 暫定では rasterizer
        //                                  側 perStampAlphaFor が alpha 固定 255 を
        //                                  返すため、alpha 筆圧連動は数値上は乗るが
        //                                  per-stamp 値が 255 で頭打ちになり可視差が
        //                                  出ないことがある — これも Step 3 で本実装.)
        //   flat   : width ✅ / alpha ❌ (固定)  (旧 `'alpha'` から rename. uniform
        //                                          alpha 塗り = stroke 全体一様濃度
        //                                          が本質. alpha 軸は palette A 値
        //                                          固定 (= max 蓄積モード + 1 回
        //                                          commit で stroke 全体 uniform
        //                                          alpha 維持). 一方、太さ軸は
        //                                          uniform alpha と独立な軸なので
        //                                          `pt.p` で動かして良い (= CSP /
        //                                          Photoshop 流のブラシ標準: 入り
        //                                          抜きで太さ変動 OK). consult
        //                                          2026-05-09 で確定.)
        //   marker : width ✅ / alpha ✅ (consult 2026-05-09 試験追加). `'flat'` と
        //                                  ほぼ同じ (= max 蓄積モード + stroke 終端
        //                                  1 回 commit) だが、alpha 軸を `pt.pAlpha`
        //                                  で動的変動させる点だけ異なる. max 蓄積
        //                                  モード下では「stroke 内で観測した最大濃さ
        //                                  で各 pixel が凍結」(= 強筆圧域は palette
        //                                  A 値 100% で平ら、弱筆圧域は palette A 値
        //                                  ×弱 % で平ら、ペン先入り抜きで離散的に
        //                                  フェード) というコピックマーカー風挙動が
        //                                  得られる. CSP / Photoshop に直接対応の
        //                                  ない独自表現.
        //   eraser : width ✅ / strength fixed   (legacy
        //                                          `WhiteboardPressureThickBrushEraserLineEvent`
        //                                          carries width/alpha both, but
        //                                          §19.15 表は太さ軸のみ flag —
        //                                          strength は `stroke.alpha`
        //                                          固定.)
        let widthScaleStart;
        let widthScaleEnd;
        let alphaScaleStart;
        let alphaScaleEnd;
        switch (stroke.pen) {
            case 'brush':
            // テクスチャブラシ (texture-brush-design.md §6): 筆圧軸は `'brush'` と
            // 同一 (width = `pt.p`、alpha = `pt.pAlpha`). texture mask は kernel 側
            // (`StampParams.texture`) で per-pixel に乗るので、ここの筆圧 scale 計算
            // には影響しない. commit cadence も `'brush'` と同じ (segment 即 commit).
            case 'texture':
            // エアブラシ (airbrush-design-2026-08-15.md §2.5): brush/texture と同じ
            // 2 軸線形 + commit cadence (segment 即 commit, `accumulate='add'`). 濃度
            // の実体は per-stamp alpha ではなく kernel 側の閾値ハッシュ判定
            // (`StampParams.airbrush`) が担うので、筆圧軸の計算自体は brush と揃える
            // のが自然 (太筆圧 = 太い径で ink 判定域が広がる、濃筆圧 = 満額 ink の
            // per-stamp alpha が高くなる).
            case 'airbrush':
            // クレヨン / パステル / ドット (phase11-light-pens-2026-08-21.md §1):
            // brush/texture/airbrush と同じ 2 軸線形. クレヨンの被覆は §2.4 のフォール
            // バックで stroke 単位の alpha から決まる (筆圧軸とは独立)。パステルは
            // alpha multiply なので alpha 軸筆圧がそのまま自然に効く。ドットは間引き
            // デコレータの内側が普通のブラシ stamp なので同じ表エントリを共有.
            case 'crayon':
            case 'pastel':
            case 'dot':
                // 2 軸独立 (consult 2026-05-09 / Step 5b): width 軸は `pt.p`、
                // alpha 軸は `pt.pAlpha` (fallback: `pt.p` → 1.0). 太さ筆圧 +
                // 濃さ筆圧 両 ON のとき widthMin と alphaMin が独立に効く.
                widthScaleStart = pPrev;
                widthScaleEnd = pNow;
                alphaScaleStart = pAlphaPrev;
                alphaScaleEnd = pAlphaNow;
                break;
            // Stage Y (consult 2026-05-31, `marker-pen-deprecation-plan.md`):
            // `'flat'` と `'marker'` は per-stamp alpha 計算が完全に同一
            // (= 両方とも widthScale = pt.p, alphaScale = pt.pAlpha). 旧仕様で
            // `'flat'` は alphaScale=1.0 固定だったが、`'flat'` の本質 (= stroke
            // 全体 uniform alpha) は **送信側で `alphaEnabled=false` 経路により
            // `pAlpha=1.0` が明示送信されること** で担保される設計に統合
            // (= `DEFAULT_PEN_PRESSURE.flat.alphaEnabled = false` (default) +
            // `computeEffectivePressure` で `{ pAlpha: 1.0 }` 明示返却). よって
            // **既存 `'flat'` ユーザの uniform alpha 挙動は完全互換**.
            //
            // 「`'flat'` で alpha 筆圧 ON にできる」設計上の意味解釈は別軸 (= UI
            // 表示は disabled で意味的に矛盾を視覚化、設定 ON でも内部 state は
            // false で固定) で運用. `'marker'` の protocol 撤去は Stage X (β 期 =
            // Phase 10) で実施予定.
            //
            // 蓄積モード (= `accumulate='max'`) + commit cadence (= stroke 終端
            // 1 回 commit) は `processPoint` 後段で共通分岐済. 「max 蓄積 + 動的
            // per-stamp」の組合せで「強筆圧域 = 平らな最大濃さ」「弱筆圧域 = 平らな
            // 弱濃さ」のキレのあるマーカー風筆致が出る (= コピックマーカー風).
            // `alphaEnabled=false` で `pAlpha=1.0` 固定なら全 pixel が palette A 値で
            // uniform に凍結 (= `'flat'` 従来挙動).
            case 'flat':
            case 'marker':
            // スクリーントーン (texture-brush-design.md §12.2): flat/marker と同じ
            // 2 軸線形. density は penParams で stroke 単位に固定するので alpha 軸の
            // 筆圧は素直に効く (筆圧グラデは不採用、consult 2026-06-20).
            case 'tone':
                widthScaleStart = pPrev;
                widthScaleEnd = pNow;
                alphaScaleStart = pAlphaPrev;
                alphaScaleEnd = pAlphaNow;
                break;
            case 'eraser':
                // alpha 軸固定 (= strength は `stroke.alpha` 一定). width 軸のみ
                // 筆圧連動.
                widthScaleStart = pPrev;
                widthScaleEnd = pNow;
                alphaScaleStart = 1;
                alphaScaleEnd = 1;
                break;
        }
        if (stroke.pen === 'eraser') {
            const strength = stroke.alpha; // 0..255, eraser uses alpha as strength
            const maskSnapshot = stroke.maskSnapshot ?? undefined;
            if (stroke.lastPoint === null) {
                eraseDot(layer, pt.x, pt.y, stroke.width, strength, widthScaleEnd, stroke.mask, maskSnapshot, this.writeClip ?? undefined);
            }
            else {
                eraseLine({
                    layer,
                    x1: stroke.lastPoint.x,
                    y1: stroke.lastPoint.y,
                    x2: pt.x,
                    y2: pt.y,
                    penWidth: stroke.width,
                    strength,
                    mask: stroke.mask,
                    maskSnapshot,
                    widthScaleStart,
                    widthScaleEnd,
                    clip: this.writeClip ?? undefined,
                });
            }
            this.expandDirtyForStroke(stroke, pt, widthScaleEnd);
            stroke.lastPoint = { x: pt.x, y: pt.y };
            stroke.lastPressure = pNow;
            // alpha 軸は eraser では使わないが、ペン切替などで stroke 状態を
            // 共有するケースに備えて (= `pt.pAlpha` が来ていれば持ち越す)
            // 一貫した carry-over を行う. `pt.pAlpha` 未指定なら既存値を維持.
            stroke.lastPressureAlpha = pt.pAlpha ?? stroke.lastPressureAlpha;
            return;
        }
        if (stroke.buffer === null)
            return;
        const penAlpha = perStampAlphaFor(stroke.pen, stroke.alpha);
        // `'flat'` (旧 `'alpha'`) wants uniform per-pixel buffer values across
        // the whole stroke so the single end-of-stroke commit produces a uniform
        // trail (architecture.md §19.3). `'brush'` keeps source-over (漸近合成)
        // to produce 中央濃く外周薄く / build-up. `'marker'` shares `'flat'`'s
        // `'max'` accumulator so each pixel freezes at the strongest per-stamp
        // alpha it ever saw — combined with the `pt.pAlpha`-driven dynamic
        // per-stamp this produces the discrete「強筆圧域 = 平らな最大濃さ」/
        // 「弱筆圧域 = 平らな弱濃さ」of the marker pen (consult 2026-05-09).
        // `'tone'` (screentone, texture-brush-design.md §12.2) joins flat/marker on
        // the `'max'` accumulator so going over the same dots never builds up — the
        // tone stays a uniform shade. `'pastel'` does NOT join this group anymore
        // (phase11-light-pens-2026-08-21.md §2.5.5) — it defers its COMMIT
        // (`stroke.flat` stays `true`) but accumulates like `'brush'` (`'add'`),
        // since its grain multiply moved off this per-dab path entirely (applied
        // once at commit instead — `stroke.commitTexture`). See
        // `accumulateModeFor`'s doc for the full split rationale.
        const accumulate = accumulateModeFor(stroke.pen);
        const maskSnapshot = stroke.maskSnapshot ?? undefined;
        if (stroke.lastPoint === null) {
            stroke.stampRenderer.dot({
                buffer: stroke.buffer,
                mainRgba: layer.main,
                maskSnapshot,
                width: layer.width,
                height: layer.height,
                x: pt.x,
                y: pt.y,
                penAlpha,
                penWidth: stroke.width,
                mask: stroke.mask,
                accumulate,
                // AA only on 加算系ペン (brush/flat/marker/texture). Eraser is handled
                // in the earlier branch and never reaches here (= 消しゴム AA はスコープ
                // 外、modern-layout.md §9.9 決定3).
                aa: stroke.aa,
                widthScaleEnd,
                alphaScaleEnd,
                // airbrush-design-2026-08-15.md §1.2: only read when
                // `pen='airbrush'`; every other pen's kernel ignores it.
                pointIndex,
                clip: this.writeClip ?? undefined,
            });
        }
        else {
            stroke.stampRenderer.line({
                buffer: stroke.buffer,
                mainRgba: layer.main,
                maskSnapshot,
                width: layer.width,
                height: layer.height,
                x1: stroke.lastPoint.x,
                y1: stroke.lastPoint.y,
                x2: pt.x,
                y2: pt.y,
                penAlpha,
                penWidth: stroke.width,
                mask: stroke.mask,
                accumulate,
                aa: stroke.aa,
                widthScaleStart,
                widthScaleEnd,
                alphaScaleStart,
                alphaScaleEnd,
                pointIndex,
                clip: this.writeClip ?? undefined,
            });
        }
        // Per-segment commit cadence (architecture.md §19.3):
        //   `'brush'`            → segment ごとに即 commit (低 buffer cost / 即時表示)
        //   `'flat'` / `'marker'` → endStroke で 1 回だけ commit
        //                          (`'flat'` = uniform alpha のため、
        //                           `'marker'` = stroke 内最大濃さで凍結する
        //                            max 蓄積モードを stroke 全体で活かすため)
        // `'tone'` (screentone) defers the single commit to stroke-end too (a
        // per-segment `'add'` commit would let crossing segments source-over on the
        // shared dot = build-up; deferring keeps the `'max'` buffer alive across the
        // whole stroke). texture-brush-design.md §12.2 (= `stroke.flat`).
        // `'pastel'` briefly deferred too (§2.5.5) but was moved BACK to the
        // per-segment cadence in §2.5.6: with one deferred commit, the commit-time
        // grain multiply caps a pixel at its texel value for the whole stroke, so
        // scribbling back and forth WHILE DRAGGING could never darken a light
        // texel — while releasing and redrawing could (each stroke commits again
        // and source-overs). Committing per segment makes both paths identical
        // (実測: ドラッグ内 4 往復 min=78 → 167、別ストローク 4 本 min=148 → 168).
        const deferToEnd = stroke.flat;
        if (!deferToEnd) {
            const bufBbox = { ...stroke.buffer.dirty };
            commitBufferToLayer({
                layer,
                buffer: stroke.buffer,
                color: stroke.color,
                userAlpha: 255,
                commitTexture: stroke.commitTexture,
            });
            this.accumulatedDirty = expandBbox(this.accumulatedDirty, normaliseBbox(bufBbox));
            this.markLayerDirty(stroke.layerId, bufBbox);
            clearStrokeBuffer(stroke.buffer);
        }
        else {
            // `'flat'` / `'marker'` deferred-commit (architecture.md §19.3): the
            // real commit fires in `endStroke`. For the per-rAF DISPLAY composite we
            // only need to re-blend the cells that could have CHANGED since the last
            // flush — i.e. the swept stamp footprint of *this* segment, NOT the
            // stroke's whole cumulative `buffer.dirty`.
            //
            // 2026-06-18 (`flat-live-composite-perf-2026-06-18.md`): the previous
            // code accumulated `stroke.buffer.dirty` here, which grows monotonically
            // over the entire stroke. The engine's `flush` then re-composited that
            // ever-growing bbox every frame (bg blend + layer copy +
            // `commitBufferToSubrect` over the full dirty, × visibleLayerCount),
            // making per-frame cost O(全塗り面積) — measured 14.7× degradation on a
            // 4000² flat stroke. Accumulating only the per-frame segment footprint
            // makes it O(新規塗り面積) (= brush-flat平坦), while the display result
            // stays bit-identical: the un-touched background was already `put` to the
            // backend on earlier frames and `compositeForDisplayRegion` re-blends ALL
            // in-flight deferred strokes inside whatever region it's handed, so
            // overlapping deferred strokes (self + remote on the same layer) are
            // re-evaluated correctly for every cell that changed this frame.
            //
            // `'max'` accumulation guarantees a cell only changes when a stamp of
            // this segment lands on it (flat: uniform alpha; marker: a higher
            // `pt.pAlpha` re-crossing), and both cases are inside this segment's
            // swept footprint. The end-of-stroke `commitBufferToLayer` is untouched
            // (it still uses the full `buffer.dirty`), so the committed pixels —
            // the only ones that reach send/snapshot — are unchanged.
            //
            // Use `max(widthScaleStart, widthScaleEnd)` for the footprint so a
            // pressure-shrinking segment (start disc wider than the end) still fully
            // covers the wider start endpoint. Over-covering only widens what gets
            // re-composited (always safe); under-covering would drop a changed cell.
            this.accumulatedDirty = expandBbox(this.accumulatedDirty, segmentFootprintBbox(layer, stroke.lastPoint, pt, stroke.width, Math.max(widthScaleStart, widthScaleEnd)));
        }
        stroke.lastPoint = { x: pt.x, y: pt.y };
        stroke.lastPressure = pNow;
        // alpha 軸の持ち越し (Step 5b / addPoints バッチ跨ぎの補間維持).
        // `pt.pAlpha` 未指定なら既存値を維持 (= 旧 client は触らない).
        stroke.lastPressureAlpha = pt.pAlpha ?? stroke.lastPressureAlpha;
    }
    expandDirtyForStroke(stroke, pt, widthScaleEnd) {
        // Eraser doesn't touch a buffer, so derive the dirty bbox from the
        // pen radius around the point itself. Use the pressure-scaled width
        // (matches what `eraseLine` actually stamped) so a low-pressure tap
        // doesn't over-invalidate. Shares `segmentFootprintBbox` with the
        // deferred-commit display path (= same swept-footprint math).
        const layer = this.layers[stroke.layerId];
        if (layer === undefined)
            return;
        const footprint = segmentFootprintBbox(layer, stroke.lastPoint, pt, stroke.width, widthScaleEnd);
        this.accumulatedDirty = expandBbox(this.accumulatedDirty, footprint);
        // The eraser writes `Layer.main` directly, so the footprint is also the
        // layer's thumbnail-dirty region.
        this.markLayerDirty(stroke.layerId, footprint);
    }
}
/**
 * Swept stamp footprint of one segment (or a single dot when `from` is
 * `null`), clamped to layer bounds. This is the bbox of every cell a single
 * `processPoint` call could have stamped: the union of the pen-radius disc
 * around both endpoints. Used by:
 *
 *   - the eraser dirty tracking (`expandDirtyForStroke`), and
 *   - the deferred-commit (`'flat'`/`'marker'`) per-rAF display invalidation
 *     (= `flat-live-composite-perf-2026-06-18.md`): only the cells this frame's
 *     segment could have changed need re-compositing, not the whole stroke.
 *
 * `effectiveWidth` uses the pressure-scaled width (`+1` round-to-half-pixel
 * margin matches the rasterizer's stamp footprint padding) so a low-pressure
 * tap doesn't over-invalidate. The disc half-extent is intentionally a hair
 * larger than `commitBufferToSubrect` actually needs — over-covering the
 * region is always safe (it only widens what gets re-composited); under-
 * covering would drop a changed cell. The `'max'` accumulator + this footprint
 * together guarantee every changed cell is inside the returned bbox.
 */
const segmentFootprintBbox = (layer, from, to, baseWidth, widthScaleEnd) => {
    const effectiveWidth = Math.max(1, Math.round(baseWidth * widthScaleEnd));
    const half = Math.ceil(effectiveWidth / 2) + 1;
    let x0 = Math.max(0, Math.floor(to.x) - half);
    let y0 = Math.max(0, Math.floor(to.y) - half);
    let x1 = Math.min(layer.width - 1, Math.floor(to.x) + half);
    let y1 = Math.min(layer.height - 1, Math.floor(to.y) + half);
    if (from !== null) {
        x0 = Math.min(x0, Math.max(0, Math.floor(from.x) - half));
        y0 = Math.min(y0, Math.max(0, Math.floor(from.y) - half));
        x1 = Math.max(x1, Math.min(layer.width - 1, Math.floor(from.x) + half));
        y1 = Math.max(y1, Math.min(layer.height - 1, Math.floor(from.y) + half));
    }
    return { x0, y0, x1, y1 };
};
const normaliseBbox = (b) => ({
    x0: b.x0,
    y0: b.y0,
    x1: b.x1,
    y1: b.y1,
});
/**
 * Rasterize the lead's short point list through the SAME stamp machinery the
 * committed stroke uses, into a transient buffer, and decimate to straight-α
 * RGBA. **No `Layer.main` write, no `accumulatedDirty` mutation** — the result
 * is display-only (modern-layout.md §9.8.1 ①).
 *
 * Contract:
 *   - `points` is the short gap「確定平滑点 → (生入力 …) → カーソル」. It must be
 *     kept short (数〜十数スタンプ) by the caller so per-frame cost is bounded
 *     (= 負荷2倍回避). The whole smoothed body is NOT re-rasterized here.
 *   - `accumulate` mirrors the per-pen commit accumulator (`brush` = 'add'
 *     source-over, `flat`/`marker` = 'max'). The lead is a tiny gap so the
 *     intra-lead overlap is one alpha accumulation only; the seam with the
 *     committed pixels is handled at composite time (single source-over).
 *   - Eraser pens have no visible lead (destination-out has no color to show);
 *     the caller should not call this for eraser, but we defensively return
 *     `null` so an eraser lead is a no-op.
 *   - `widthScale` / `alphaScale` are left at the default (1) — the lead shows
 *     the pen at its base size (pressure on the未確定 gap is best-effort; the
 *     caller may pass per-point `p` / `pAlpha` on the points which the sweep
 *     interpolates, same as the body).
 *
 * `layerWidth` / `layerHeight` are the engine's current layer dims, used to
 * clamp the transient buffer's bbox exactly as the committed path does.
 */
export const rasterizeLeadSegment = (args) => {
    const { pen, layerId, layerWidth, layerHeight, points } = args;
    if (pen.pen === 'eraser')
        return null; // no visible lead for destination-out
    if (points.length === 0)
        return null;
    if (layerWidth <= 0 || layerHeight <= 0)
        return null;
    const buffer = createStrokeBuffer(layerWidth, layerHeight);
    const penAlpha = perStampAlphaFor(pen.pen, pen.alpha);
    // Same accumulator the committed stroke uses (§19.3 / §2.5.5,
    // `accumulateModeFor`): brush/texture/crayon/airbrush/dot/pastel = source-
    // over, flat/marker/tone = max. Using the committed stroke's own resolver
    // here (rather than a second duplicated literal) keeps the lead preview and
    // the commit path from drifting out of sync.
    const accumulate = accumulateModeFor(pen.pen);
    // Commit-time texture (phase11-light-pens-2026-08-21.md §2.5.5): the lead's
    // own decimation loop below is effectively "committing" this transient
    // buffer (it's never fed through `commitBufferToLayer`), so it must apply
    // the SAME commit-time texture multiply or a pastel lead would preview
    // untextured while the committed body shows grain — see this function's
    // header comment ("見えが一致する") for why that mismatch is not acceptable.
    const commitTexture = resolveCommitTexture(pen.pen, pen.penParams);
    const mask = args.mask ?? NO_LEAD_MASK;
    // mainRgba is only read by the mask compare; supply an empty fallback so the
    // (mask='none' default) path never dereferences it.
    const mainRgba = args.mainRgba ?? EMPTY_LEAD_MAIN;
    // Resolve the SAME pen kernel the committed stroke uses (texture-brush-design
    // .md §5) so the live lead preview matches the body byte-for-byte (texture +
    // preset included). Non-texture pens resolve to the JS kernel (= unchanged).
    const stampRenderer = resolveStampRenderer(pen.pen, pen.penParams, pen.alpha);
    let last = null;
    // airbrush-design-2026-08-15.md §9 (要検証, resolved): the lead preview
    // DOES route through `resolveStampRenderer`, so an airbrush lead needs SOME
    // `pointIndex` — but this function has no access to the committed stroke's
    // true wire-point counter (it's called fresh, transient, per rAF tick with
    // just the short un-committed gap). We use a LOCAL 0-based counter scoped
    // to just this call so at least the points WITHIN one lead vary their hash
    // input (avoids a static repeated pattern across the gap's dabs). This does
    // NOT match the eventual committed `StrokeState.pointIndex` the same points
    // will get once they're actually sent — but that's fine: the lead is
    // display-only (never committed to `Layer.main`, never sent over the wire,
    // see the header comment), so there is no parity contract to keep. The
    // visible effect is limited to a possible one-frame grain-pattern "shift"
    // exactly when a lead segment commits, well within the noise of a spray.
    let leadPointIndex = 0;
    for (const pt of points) {
        const pNow = pt.p;
        const pAlphaNow = pt.pAlpha;
        const pointIndex = leadPointIndex;
        leadPointIndex += 1;
        if (last === null) {
            stampRenderer.dot({
                buffer,
                mainRgba,
                width: layerWidth,
                height: layerHeight,
                x: pt.x,
                y: pt.y,
                penAlpha,
                penWidth: pen.width,
                mask,
                accumulate,
                aa: pen.aa,
                widthScaleEnd: pNow ?? 1,
                alphaScaleEnd: pAlphaNow ?? pNow ?? 1,
                pointIndex,
            });
        }
        else {
            stampRenderer.line({
                buffer,
                mainRgba,
                width: layerWidth,
                height: layerHeight,
                x1: last.x,
                y1: last.y,
                x2: pt.x,
                y2: pt.y,
                penAlpha,
                penWidth: pen.width,
                mask,
                accumulate,
                aa: pen.aa,
                widthScaleStart: last.p ?? 1,
                widthScaleEnd: pNow ?? 1,
                alphaScaleStart: last.pAlpha ?? last.p ?? 1,
                alphaScaleEnd: pAlphaNow ?? pNow ?? 1,
                pointIndex,
            });
        }
        last = { x: pt.x, y: pt.y, p: pNow, pAlpha: pAlphaNow };
    }
    // Decimate the dirty sub-rect of the transient buffer to straight-α RGBA.
    const { x0, y0, x1, y1 } = buffer.dirty;
    if (x0 < 0)
        return null; // nothing painted
    const w = x1 - x0 + 1;
    const h = y1 - y0 + 1;
    const { x0: bx0, y0: by0, x1: bx1 } = buffer.bbox;
    if (bx0 < 0)
        return null;
    const bboxW = bx1 - bx0 + 1;
    const r = (pen.color >> 16) & 0xff;
    const g = (pen.color >> 8) & 0xff;
    const b = pen.color & 0xff;
    const rgba = new Uint8ClampedArray(new ArrayBuffer(w * h * 4));
    const alpha16 = buffer.alpha16;
    let painted = false;
    for (let y = 0; y < h; y++) {
        const bufRow = (y0 + y - by0) * bboxW;
        const outRow = y * w * 4;
        for (let x = 0; x < w; x++) {
            const a16 = alpha16[bufRow + (x0 + x - bx0)] ?? 0;
            if (a16 === 0)
                continue;
            // 8.8 fixed-point → 8-bit straight alpha (round-to-nearest, clamp) —
            // same decimation the commit path uses (`commitBufferToLayer`).
            const a8raw = (a16 + 128) >> 8;
            let a = a8raw > 255 ? 255 : a8raw;
            // Commit-time texture multiply (§2.5.5, see `commitTexture` above) —
            // sampled at the pixel's own CANVAS coords (`x0 + x`, `y0 + y`), same
            // as `commitBufferToLayer`'s `sampleTextureAlpha(commitTexture, x, y)`.
            if (commitTexture !== undefined) {
                const t = sampleTextureAlpha(commitTexture, x0 + x, y0 + y);
                a = Math.round((a * t) / 255);
            }
            if (a === 0)
                continue;
            const o = outRow + x * 4;
            rgba[o] = r;
            rgba[o + 1] = g;
            rgba[o + 2] = b;
            rgba[o + 3] = a;
            painted = true;
        }
    }
    if (!painted)
        return null;
    return { rgba, x: x0, y: y0, width: w, height: h, layerId };
};
const NO_LEAD_MASK = { type: 'none', colors: [] };
const EMPTY_LEAD_MAIN = new Uint8ClampedArray(new ArrayBuffer(0));
//# sourceMappingURL=strokeRenderer.js.map